// app/utils/authcontext.tsx
'use client';

import { createContext, useContext, useState, ReactNode } from 'react';
import Cookies from 'js-cookie';
import * as Global from '@utils/global';
import { useRouter } from 'next/navigation';
import { useEffect } from "react";
import axios from "axios";

// ===============================
// 型定義
// ===============================
interface AuthContextProps {
  domainTitle: string | null;
  employeeGUID: string | null;
  employeeDomain: string | null;
  employeeName: string | null;
  employeeFlag: string | null;
  setDomainTitle: (domain_title: string | null) => void;
  setEmployeeGUID: (user_guid: string | null) => void;
  setEmployeeDomain: (domain: string | null) => void;
  setEmployeeName: (user_name: string | null) => void;
  setEmployeeFlag: (user_inner_flag: string | null) => void;
  loaded: boolean;
}

// ===============================
// コンテキスト本体
// ===============================
const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [domainTitle, setDomainTitle] = useState<string | null>(null);
  const [employeeGUID, setEmployeeGUID] = useState<string | null>(null);
  const [employeeDomain, setEmployeeDomain] = useState<string | null>(null);
  const [employeeName, setEmployeeName] = useState<string | null>(null);
  const [employeeFlag, setEmployeeFlag] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const domain_title = Cookies.get(Global.COOKIE_DOMAIN_TITLE) ?? null;
    const user_guid = Cookies.get(Global.COOKIE_EMPLOYEE_GUID) ?? null;
    const domain = Cookies.get(Global.COOKIE_EMPLOYEE_DOMAIN) ?? null;
    const user_name = Cookies.get(Global.COOKIE_EMPLOYEE_NAME) ?? null;
    const user_inner_flag = Cookies.get(Global.COOKIE_EMPLOYEE_FLAG) ?? null;

    if (user_guid && user_name) {
      setDomainTitle(domain_title);
      setEmployeeGUID(user_guid);
      setEmployeeDomain(domain);
      setEmployeeName(user_name);
      setEmployeeFlag(user_inner_flag);
    }

    setLoaded(true);
  }, []);

  // 🚨 ロード完了まで描画を止める（SSRの不整合防止）
  if (!loaded) return null;

  return (
    <AuthContext.Provider
      value={{
        domainTitle,
        setDomainTitle,
        employeeGUID,
        setEmployeeGUID,
        employeeDomain,
        setEmployeeDomain,
        employeeName,
        setEmployeeName,
        employeeFlag,
        setEmployeeFlag,
        loaded,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ===============================
// useAuth(): 認証情報アクセス
// ===============================
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return {
    ...ctx,
    // 社員判定
    isEmployee: ctx.employeeFlag ,
  };
};

// ===============================
// useAuthRedirect(): 認証リダイレクト
// ===============================
export const useAuthRedirect = () => {
  const { 
    domainTitle,
    employeeGUID,
    employeeDomain, 
    setDomainTitle, 
    setEmployeeDomain, 
    setEmployeeName, 
    setEmployeeGUID, 
    setEmployeeFlag 
  } = useAuth();
  const router = useRouter();

  useEffect(() => {
    const checkValidSession = async () => {
      // GUIDなし → ログイン画面へ
      if (!employeeGUID) {
        router.push("/signon");
        return;
      }

      // 🔒 一度検証済みならスキップ
      const verified = sessionStorage.getItem("employeeVerified");
      if (verified === "1") return;

      try {
        const res = await axios.post<any>(`${Global.getApiBaseUrl()}/personal/me`, {
          base_interface: {
            user_guid: employeeGUID,
            user_domain: employeeDomain,
            ip_address: window.location.hostname,
            user_agent: navigator.userAgent,
          }
        });

        if (res.data.user_name) {
          setEmployeeName(res.data.user_name);
          setEmployeeFlag(res.data.user_inner_flag);
          sessionStorage.setItem("employeeVerified", "1");
        }
      } catch (e) {
        // 🔄 認証失敗 → ログアウト処理
        Cookies.remove(Global.COOKIE_DOMAIN_TITLE);
        Cookies.remove(Global.COOKIE_EMPLOYEE_GUID);
        Cookies.remove(Global.COOKIE_EMPLOYEE_DOMAIN);
        Cookies.remove(Global.COOKIE_EMPLOYEE_NAME);
        Cookies.remove(Global.COOKIE_EMPLOYEE_FLAG);
        setDomainTitle(null);
        setEmployeeGUID(null);
        setEmployeeDomain(null);
        setEmployeeName(null);
        setEmployeeFlag(null);
        sessionStorage.removeItem("employeeVerified");
        router.push("/signon");
      }
    };

    checkValidSession();
  }, [employeeGUID, router]);
};
